<template>
  <div class="w-100">
    <proa-card class="h-100 d-flex flex-column" no-padding>
      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <proa-back-button
            @onClick="onClickBackButton"
          />
          <proa-heading class="group-chat-name" detail>{{ $t('pages.management.sharewindSettings.memberManagement') }}</proa-heading>
          <div>
            <span class="text-secondary font-weight-w6">{{ $t('pages.indexManagementMembers.affiliatedMembers') }}</span>
            <span v-if="totalUsersCount" class="ml-2">{{ `${totalUsersCount} / ${currentLicensedPlan.maxUsers}` }}</span>
          </div>
        </div>
      </template>
      <div class="search-form-wrapper">
        <proa-search-input
          v-model="searchUserName"
          type="text"
          class="search-input"
          :placeholder="$t('common.searchForMembers')"
          dark
        />
      </div>
      <div class="content-container">
        <div class="table-outer-wrapper">
          <div class="table-inner-scroll-wrapper">
            <table class="table-users">
              <thead>
                <tr>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.user.attributes.displayName') }}</span>
                    </div>
                  </th>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.user.attributes.email') }}</span>
                    </div>
                  </th>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.user.attributes.role') }}</span>
                    </div>
                  </th>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.user.attributes.userOrganizationStatus') }}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="inviteUser in inviteUsers"
                  :key="'invited' + inviteUser.id"
                  class="invite"
                  @click="onClickInvitedMemberDetail(inviteUser.id)"
                >
                  <td>
                    <proa-avatar :src="require('@/assets/images/avatar_default.png')" size="sm"/>
                    <span class="ml-3">{{ $t('models.inviteUser.displayName') }}</span>
                  </td>
                  <td>{{ inviteUser.email }}</td>
                  <td>
                    {{ $t(`common.roleType.${inviteUser.role().roleType}`) }}
                  </td>
                  <td :class="['invite-state', inviteUser.inviteState]">{{ $t(`models.inviteUser.inviteStates.${inviteUser.inviteState}`) }}</td>
                </tr>
                <tr
                  v-for="user in users"
                  :key="user.id"
                  @click="onClickMemberDetail(user.id)"
                >
                  <td>
                    <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
                    <span class="ml-3">{{ user.meta.displayName }}</span>
                  </td>
                  <td>{{ user.email }}</td>
                  <td>
                    {{ $t(`common.roleType.${currentOrganization.adminUserId == user.id ? 'admin' : user.currentOrganizationRole().roleType}`) }}
                  </td>
                  <td>{{ $t('models.user.inUse') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="empty-footer"></div>
    </proa-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ManagedUser from '@/store/models/admin/ManagedUser'
import InviteUser from '@/store/models/admin/InviteUser'

export default {
  name: 'IndexManagementMembers',
  computed: {
    totalUsersCount: function() {
      return ManagedUser.query().count() + InviteUser.query().count()
    },
    searchUserName: {
      get () {
        return this.innerSearchUserName
      },
      set (value) {
        if (value) {
          this.inviteUsers = InviteUser.query().where((inviteUser) => {
            return inviteUser.email.indexOf(value) != -1
          }).get()
          this.users = ManagedUser.query().where((user) => {
            return user.meta.displayName.indexOf(value) != -1 || user.email.indexOf(value) != -1
          }).get()
        } else {
          this.setDefaultInviteUsers()
          this.setDefaultUsers()
        }
        this.innerSearchUserName = value
      }
    },
    ...mapGetters({
      currentOrganization: 'auth/currentOrganization',
      currentLicensedPlan: 'auth/currentLicensedPlan'
    })
  },
  data() {
    return {
      users: [],
      inviteUsers: [],
      innerSearchUserName: ''
    }
  },
  methods: {
    setDefaultUsers: function() {
      this.users = ManagedUser.all()
    },
    setDefaultInviteUsers: function() {
      this.inviteUsers = InviteUser.all()
    },
    onClickBackButton: function() {
      this.$router.push({ name: 'management' })
    },
    onClickInvitedMemberDetail: function(id) {
      this.$router.push({ name: 'showInvitedMembers', params: { id: id } })
    },
    onClickMemberDetail:function(id) {
      this.$router.push({ name: 'showManagementMembers', params: { id: id } })
    },
    ...mapActions({
      apiGetManagedUsers: 'entities/managedUsers/index',
      apiGetInviteUsers: 'entities/inviteUsers/index'
    })
  },
  async created() {
    this.setDefaultInviteUsers()
    this.setDefaultUsers()

    await this.apiGetInviteUsers()
    await this.apiGetManagedUsers()

    this.setDefaultInviteUsers()
    this.setDefaultUsers()
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.search-form-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 10px 20px;

  .search-input {
    width: 200px;
    font-size: 12px;
  }
}

.content-container {
  flex-grow: 1;

  .table-outer-wrapper {
    position: relative;
    height: 100%;
  }

  .table-inner-scroll-wrapper {
    position: absolute;
    max-height: 100%;
    width: 100%;
    overflow-y: auto;
  }

  .table-users {
    height: 100%;
    min-width: 100%;
    white-space: nowrap;

    thead {
      border-bottom: 1px solid $dark-border-color;

      &:after {
        content: "";
        display: block;
        height: 10px;
      }

      th {
        position: sticky;
        top: 0;
        background-color: white;

        &:last-child {
          padding-right: 20px;
        }

        .attribute-name {
          background-color: $light-gray-color;
          background-clip: content-box;
          padding-left: 20px;

          span {
            display: block;
            padding: 7px 5px;
            font-size: 13px;
            font-weight: 300;
            color: $secondary-color;
          }
        }
      }
    }

    tbody tr {
      cursor: pointer;
      border-bottom: 1px solid $light-border-color;

      &:hover {
        background: $light-gray-color;
      }

      &.invite {
        td {
          color: $light-text-color;
        }
        .invite-state {
          &.invite {
            color: $link-color;
          }

          &.rejected {
            color: $error-color;
          }
        }
      }

      td {
        padding: 20px 0 20px 20px;
      }
    }
  }
}

.empty-footer {
  height: 60px;
  border-top: 1px solid $dark-border-color;
}
</style>
